import React, { useState, useEffect } from 'react';
import { 
  ShoppingBag, 
  ArrowUpRight, 
  ShieldCheck, 
  FileCode2, 
  Star
} from 'lucide-react';
import { CustomerPurchase } from '../types/productBuilder';

interface MyPurchasesViewProps {
  onCustomizeTemplate: (templateId: string) => void;
}

export const MyPurchasesView: React.FC<MyPurchasesViewProps> = ({
  onCustomizeTemplate
}) => {
  const [purchases, setPurchases] = useState<CustomerPurchase[]>([]);

  // On mount, load purchased digital templates from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('nexorax_saved_purchases');
    if (saved) {
      setPurchases(JSON.parse(saved));
    } else {
      // Create seed data if empty
      const seeds: CustomerPurchase[] = [
        {
          id: 'pur-1',
          templateId: 'ndp-1',
          productName: 'AI Business Website Starter',
          previewImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800',
          purchaseDate: new Date(Date.now() - 345600000).toLocaleDateString(), // 4 days ago
          licenceType: 'Business Use Certificate',
          pricePaid: 99,
          licencePermitsEditing: true
        }
      ];
      setPurchases(seeds);
      localStorage.setItem('nexorax_saved_purchases', JSON.stringify(seeds));
    }
  }, []);

  return (
    <div className="space-y-6 text-left">
      <div>
        <h2 className="text-xl font-sans font-black text-white uppercase tracking-tight">Contract &amp; Asset Vault</h2>
        <p className="text-xs text-slate-400 mt-1">Access verified SaaS templates, codepacks, and design assets that are locked to your hardware. Launch customize sprint natively.</p>
      </div>

      {purchases.length === 0 ? (
        <div className="p-12 text-center border border-white/5 rounded-3xl bg-slate-950/30">
          <ShoppingBag className="w-12 h-12 mx-auto text-zinc-600 mb-3 stroke-[1.5]" />
          <h3 className="text-sm font-semibold text-slate-300 font-mono uppercase">Vault currently empty</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1">Once you buy code packs, layouts, or automate assets from the Digital Products matrix, they will instantly display in your secure vault.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {purchases.map((pur) => (
            <div 
              key={pur.id}
              className="p-5 rounded-3xl bg-[#02050f]/60 hover:bg-[#02050f]/80 border border-white/5 hover:border-slate-800 transition-all flex flex-col md:flex-row gap-5"
            >
              <div className="w-full md:w-36 h-28 rounded-2xl overflow-hidden border border-white/5 shrink-0 select-none">
                <img src={pur.previewImage} alt={pur.productName} referrerPolicy="no-referrer" className="w-full h-full object-cover" />
              </div>

              <div className="flex-1 flex flex-col justify-between text-left space-y-3">
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-[9px] font-mono uppercase text-zinc-500">
                    <span>Acquired: {pur.purchaseDate}</span>
                    <span className="text-[#00ffff] font-extrabold">Price: £{pur.pricePaid}</span>
                  </div>
                  <h3 className="text-sm font-bold text-white">{pur.productName}</h3>
                  <div className="flex items-center gap-1 text-[10px] font-mono text-slate-400">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Licence: {pur.licenceType}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {pur.licencePermitsEditing ? (
                    <button
                      onClick={() => onCustomizeTemplate(pur.templateId)}
                      className="flex-1 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-mono text-[10.5px] font-black uppercase tracking-widest rounded-xl transition-all shadow-[0_0_15px_rgba(236,72,153,0.25)] flex items-center justify-center gap-1 cursor-pointer"
                    >
                      <span>Open in Product Builder</span>
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <div className="flex-1 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-center font-mono text-[9.5px] text-zinc-400 select-none">
                      Licence Restricts Customizing
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
